'use client';

import * as React from 'react';
import {
  ContentCard,
  ContentPlaceholderCard
} from '@purpose/ui/components/ContentCard';
import {SimpleRoundedCard} from '@purpose/ui/components/specialized-carousels';
import {cn} from '@purpose/ui/lib/utils';
import {useResponsive} from '@purpose/ui/hooks';

interface ResponsiveGridCard {
  id: number;
  organisation_id?: number;
  logo: string;
  name: string;
  description?: string;
  subtitle?: string;
  business_number?: string;
  organisation_name?: string;
  is_multiple_organisation?: boolean;
  organisations?: {id: number; name: string; logo: string | null}[];
}

interface ResponsiveGridProps {
  cards?: ResponsiveGridCard[];
  onCardClick?: (id: number, orgId?: number) => void;
  title?: string | null;
  titleColor?: string;
  sideText?: string;
  withoutDescription?: boolean;
  getDescription?: (card: ResponsiveGridCard) => string | undefined;
  sideButtonProps?: {
    title: string;
    onClick: () => void;
  } | null;
  maxVisibleCards?: number;
  roundCards?: boolean;
  smallRoundCards?: boolean;
  categoryDesign?: boolean;
  fullWidthRow?: boolean;
  overtitle?: (b: any) => React.JSX.Element;
  placeholderProps?: {
    withTitle?: boolean;
    withSubtitle?: boolean;
    description?: string;
    buttonProps?: {
      title: string;
      Icon?: any;
      onClick: () => void;
    };
  };
}

const ResponsiveGrid = React.forwardRef<HTMLDivElement, ResponsiveGridProps>(
  (
    {
      cards,
      title,
      titleColor,
      sideText,
      onCardClick,
      getDescription,
      withoutDescription,
      sideButtonProps,
      maxVisibleCards = 6,
      roundCards,
      smallRoundCards,
      categoryDesign,
      fullWidthRow = false,
      overtitle = () => <></>,
      placeholderProps
    },
    ref
  ) => {
    const {breakpoint} = useResponsive();
    if (!cards?.length) return null;

    const slidesPerView = {
      xs: 2,
      sm: 3,
      md: 4,
      lg: 5,
      xl: 5
    };
    const placeholderCardNumber = Math.max(
      0,
      // @ts-ignore
      slidesPerView?.[breakpoint] - (cards.length % slidesPerView?.[breakpoint]) // check remainder after some rows have been filled
    );
    const showPlaceholderCards = !!placeholderProps && placeholderCardNumber;

    // Calculate grid columns based on smallRoundCards and fullWidthRow
    const getGridCols = () => {
      if (fullWidthRow) {
        return `grid-cols-${cards.length}`;
      }
      if (roundCards && !smallRoundCards)
        return `grid-cols-2 md:grid-cols-3 lg:grid-cols-5`;

      if (smallRoundCards) {
        return `grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-${cards.length}`;
      }
      return ``;
    };

    // Calculate gap based on smallRoundCards and fullWidthRow
    const getGridGap = () => {
      if (fullWidthRow || smallRoundCards) {
        return 'gap-2';
      }
      return 'gap-4';
    };

    return (
      <div className="w-full" ref={ref}>
        {title && (
          <div className="mb-4 flex items-center justify-between">
            <h3
              className="text-2xl leading-none font-semibold tracking-tight"
              style={{color: titleColor}}
            >
              {title}
            </h3>
            <div className="flex items-center gap-2">
              {sideText && (
                <p className="text-muted-foreground text-sm">{sideText}</p>
              )}
              {sideButtonProps && (
                <button
                  aria-label={sideButtonProps.title}
                  onClick={sideButtonProps.onClick}
                  className="text-primary bg-secondary/10 hover:bg-secondary/20 rounded-md border-none px-3 py-2 text-sm font-medium shadow-none transition-colors"
                >
                  {sideButtonProps.title}
                </button>
              )}
            </div>
          </div>
        )}

        <div
          style={
            !getGridCols()?.length
              ? {
                  gridTemplateColumns: `repeat(${slidesPerView[breakpoint]}, minmax(0, 1fr))`
                }
              : undefined
          }
          className={cn('grid', getGridCols(), getGridGap())}
        >
          {cards.map((card, index) => (
            <div
              key={`${card.id}-${index}`}
              className={cn(
                fullWidthRow && 'w-full',
                smallRoundCards && 'w-full'
              )}
            >
              {!roundCards && !smallRoundCards ? (
                <ContentCard
                  image={(card.logo !== '' && card.logo) || null}
                  title={card.name}
                  subtitle={
                    card.business_number
                      ? `Org No ${card.business_number}`
                      : card.subtitle
                  }
                  overtitle={overtitle?.(card)}
                  description={
                    !withoutDescription &&
                    (getDescription?.(card) || card.description)
                      ? getDescription?.(card) || card.description
                      : undefined
                  }
                  priority={index < maxVisibleCards}
                  onClick={
                    onCardClick
                      ? () => onCardClick(card.id, card?.organisation_id)
                      : undefined
                  }
                />
              ) : (
                <div
                  className={cn(
                    smallRoundCards &&
                      'flex h-full w-full flex-col items-center justify-center'
                  )}
                >
                  <SimpleRoundedCard
                    image={(card.logo !== '' && card.logo) || null}
                    title={card.name}
                    subtitle={
                      card.business_number
                        ? `Org No ${card.business_number}`
                        : card.subtitle
                    }
                    overtitle={overtitle?.(card)}
                    description={
                      !withoutDescription &&
                      (getDescription?.(card) || card.description)
                        ? getDescription?.(card) || card.description
                        : undefined
                    }
                    small={smallRoundCards}
                    categoryDesign={categoryDesign}
                    width="100%"
                    onClick={
                      onCardClick
                        ? () => onCardClick(card.id, card?.organisation_id)
                        : undefined
                    }
                  />
                </div>
              )}
            </div>
          ))}
          {showPlaceholderCards &&
            Array.from({length: placeholderCardNumber}).map((item, index) => (
              <div
                className="bg-card relative flex w-full flex-col gap-1 overflow-hidden rounded-lg"
                key={`${cards?.[0]?.id}-placeholder-${index}`}
              >
                <ContentPlaceholderCard {...placeholderProps} />
              </div>
            ))}
        </div>
      </div>
    );
  }
);

ResponsiveGrid.displayName = 'ResponsiveGrid';

export {ResponsiveGrid};
export type {ResponsiveGridProps, ResponsiveGridCard};
